import type { TAddressTextFieldProps } from "@inputs/autocomplete/Address";
import { Autocomplete, Box, CircularProgress, Divider, Link, Stack, TextField, Typography } from "@mui/material";
import { Fragment, type HTMLAttributes } from "react";
import { type Control, Controller, type FieldValues, type Path } from "react-hook-form";
import { type IAddressOptions, useAddressMeeting } from "./logics";

export interface IAddressMeetingProps<T extends FieldValues, TTransformedValues = T> {
  control: Control<T, void, TTransformedValues>;
  name: Path<T>;
  label?: string;
  TextFieldProps?: TAddressTextFieldProps;
  disableGoogle?: true;
  hideComplementaryAddress?: boolean;
}

const renderText = (hasFavorites: boolean) => {
  if (hasFavorites) {
    return "Pour gérer les adresses de cette liste, rendez-vous dans votre page ";
  }
  return "Aucune adresse pour le moment. Pour gérer les adresses de cette liste, rendez-vous dans votre page ";
};

const FavoriteHeader = ({ accountUrl, hasFavorites }: { accountUrl: string; hasFavorites: boolean }) => (
  <Box sx={{ py: 1 }}>
    <Typography variant="body1SemiBold" color="primary">
      Adresses enregistrées
    </Typography>
    <Typography variant="body1" color="primary">
      {renderText(hasFavorites)}
      <Link
        href={accountUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ fontWeight: "bold", textDecoration: "underline", color: "inherit" }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        Mon compte
      </Link>
      .
    </Typography>
  </Box>
);

const FavoriteOption = ({ option }: { option: IAddressOptions }) => (
  <Stack sx={{ width: "100%" }}>
    <Typography
      variant="body1"
      sx={{
        color: "text.primary",
      }}
    >
      {option.formatted}
    </Typography>
    {option.complementaryAddress && (
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
        }}
      >
        {option.complementaryAddress}
      </Typography>
    )}
  </Stack>
);

const renderOptionLabel = (option: IAddressOptions | null): string => {
  if (option) {
    let formatted = option.formatted || "";
    if (formatted === "") {
      if (option.city && option.postalCode) {
        formatted = `${option.city} (${option.postalCode})`;
      } else if (option.city) {
        formatted = option.city;
      }
    }
    return formatted;
  }
  return "";
};

const renderOption = (
  optionProps: HTMLAttributes<HTMLLIElement> & {
    key: unknown;
  },
  option: IAddressOptions | null,
  options: IAddressOptions[],
  inputValue: string,
  accountUrl: string,
  hasFavorites: boolean,
  apiHookLoading: boolean
) => {
  if (!option) {
    return null;
  }
  const firstFavoriteIndex = options.findIndex((o) => o.isFavorite);
  const isFirstFavorite = option.isFavorite && options.indexOf(option) === firstFavoriteIndex;
  const hasSearchResults = firstFavoriteIndex > 0;
  const noApiResults = options.findIndex((o) => !o.isFavorite) === -1;

  if (option.isFavorite) {
    return (
      <Fragment key={`fav-${option.formatted}`}>
        {apiHookLoading && <CircularProgress size={15} />}
        {noApiResults && inputValue.length > 2 && !apiHookLoading && (
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
            }}
          >
            Pas de résultat
          </Typography>
        )}
        {isFirstFavorite && (
          <Stack sx={{ mx: 2 }}>
            {hasSearchResults && <Divider sx={{ my: 1 }} />}
            <FavoriteHeader accountUrl={accountUrl} hasFavorites={hasFavorites} />
          </Stack>
        )}
        <li {...optionProps} key={option.formatted}>
          <FavoriteOption option={option} />
        </li>
      </Fragment>
    );
  }
  return (
    <li {...optionProps} key={option.formatted}>
      <Typography variant="body1">{option.formatted}</Typography>
    </li>
  );
};

const noOption = (apiHookLoading: boolean, accountUrl: string, hasFavorites: boolean) => (
  <Stack>
    {apiHookLoading ? (
      <CircularProgress size={15} />
    ) : (
      <>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
          }}
        >
          Pas de résultat
        </Typography>
        <FavoriteHeader accountUrl={accountUrl} hasFavorites={hasFavorites} />
      </>
    )}
  </Stack>
);

export const AddressMeeting = <T extends FieldValues, TTransformedValues = T>({
  control,
  name,
  label,
  TextFieldProps,
  disableGoogle,
  hideComplementaryAddress,
}: IAddressMeetingProps<T, TTransformedValues>) => {
  const { options, favoriteOptions, apiHookLoading, handleInputChange, accountUrl, inputValue } =
    useAddressMeeting(disableGoogle);

  const hasFavorites = favoriteOptions.length > 0;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Autocomplete
            value={value ? { ...value, isFavorite: false } : null}
            onChange={(_, newValue) => {
              if (newValue) {
                const { isFavorite, ...address } = newValue;
                onChange(address);
              } else {
                onChange(null);
              }
            }}
            options={options}
            renderInput={(params) => (
              <TextField
                label={label}
                type="text"
                {...TextFieldProps}
                {...params}
                size="small"
                error={!!error}
                helperText={error?.message}
                sx={{
                  "#input-address-helper-text": {
                    margin: 0,
                  },
                }}
              />
            )}
            onInputChange={handleInputChange}
            getOptionLabel={(option) => renderOptionLabel(option)}
            renderOption={(optionProps, option) =>
              renderOption(optionProps, option, options, inputValue, accountUrl, hasFavorites, apiHookLoading)
            }
            isOptionEqualToValue={(option, val) => Boolean(option && val && option.formatted === val.formatted)}
            filterOptions={(opts) => opts}
            noOptionsText={noOption(apiHookLoading, accountUrl, hasFavorites)}
            sx={{
              listbox: {
                maxHeight: 300,
              },
            }}
          />
          {!hideComplementaryAddress && (
            <TextField
              size="small"
              label="Complément d'adresse (étage, bâtiment, entreprise...)"
              value={value?.complementaryAddress ?? ""}
              onChange={(e) => value && onChange({ ...value, complementaryAddress: e.target.value })}
              disabled={!value}
              slotProps={{ htmlInput: { maxLength: 38 } }}
            />
          )}
        </Stack>
      )}
    />
  );
};
